import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import i18n from '../../locales/i18n';
import { createUseStyles } from 'react-jss';
import {
    Button as MuiButton,
    ButtonGroup,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Skeleton,
    Autocomplete,
    TextField,
} from '@mui/material';
import { Business, Groups, BarChartOutlined, PieChartOutlined } from '@mui/icons-material';
import useSWR from 'swr';
import { fetcher, quietFetcher, dashboardApi, companyApi, groupApi } from '../../lib/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import MapboxMap from '../../components/MapboxMap/MapboxMap';
import AnalyticsLive from './AnalyticsLive/AnalyticsLive';
import { useThemeMode } from '../../theme/ThemeProvider';

const readCssVar = (name, fallback) => {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
};

import GridContainer from '../../components/Grid/GridContainer.js';
import GridItem from '../../components/Grid/GridItem.js';
import Card from '../../components/Card/Card.js';
import CardBody from '../../components/Card/CardBody.js';
import CardHeader from '../../components/Card/CardHeader.js';
import CardFooter from '../../components/Card/CardFooter.js';
import Table from '../../components/Table/Table.js';
import SpecialUserCard from 'components/SpecialUserCard/SpecialUserCard';
import formatWithThousandSeparator from 'utils/thousand_separator';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip as ChartJSTooltip,
    Legend as ChartJSLegend,
    Filler,
} from 'chart.js';
import { Bar as ChartJSBar, Pie as ChartJSPie } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-plugin-datalabels';
import SparaLoader from 'components/SparaLoader/SparaLoader';
import SparaButton from 'components/SparaButton/SparaButton';
import { FlexContainer } from 'components/FlexContainer/FlexContainer';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    ChartJSTooltip,
    ChartJSLegend,
    Filler,
    zoomPlugin
);

const useStyles = createUseStyles({
    buttonGroup: {
        marginTop: '8px',
        display: 'flex',
        gap: '8px',
        '& .MuiButton-root': {
            width: '80px',
            borderRadius: '16px',
            textTransform: 'none',
            padding: '2px 8px',
            fontSize: '0.75rem',
            '&.MuiButton-contained': {
                backgroundColor: '#007AAA',
                '&:hover': {
                    backgroundColor: '#005f7f',
                },
            },
            '&.MuiButton-outlined': {
                borderColor: 'var(--spara-offer-well-text, #007AAA)',
                color: 'var(--spara-offer-well-text, #007AAA)',
                '&:hover': {
                    borderColor: 'var(--spara-offer-well-text, #005f7f)',
                    backgroundColor: 'var(--spara-offer-well, rgba(0, 122, 170, 0.04))',
                },
            },
        },
    },
        dateControls: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '8px',
        padding: '12px',
        backgroundColor: 'var(--spara-surface-2, #f8f9fa)',
        borderRadius: '8px',
        border: '1px solid var(--spara-border, #e9ecef)',
    },
    dateRangeButtons: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        '& .MuiButton-root': {
            fontSize: '0.75rem',
            padding: '4px 12px',
            borderRadius: '12px',
            textTransform: 'none',
            minWidth: 'auto',
            '&.MuiButton-contained': {
                backgroundColor: '#007AAA',
                '&:hover': {
                    backgroundColor: '#005f7f',
                },
            },
            '&.MuiButton-outlined': {
                borderColor: 'var(--spara-offer-well-text, #007AAA)',
                color: 'var(--spara-offer-well-text, #007AAA)',
                '&:hover': {
                    borderColor: 'var(--spara-offer-well-text, #005f7f)',
                    backgroundColor: 'var(--spara-offer-well, rgba(0, 122, 170, 0.04))',
                },
            },
        },
    },
    dateInputs: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        '& .react-datepicker-wrapper': {
            flex: 1,
            minWidth: '120px',
        },
        '& .react-datepicker__input-container': {
            width: '100%',
        },
        '& .react-datepicker__input-container input': {
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid var(--spara-input-border, #ddd)',
            fontSize: '0.85rem',
            color: 'var(--spara-text-muted, #666)',
            backgroundColor: 'var(--spara-input-bg, white)',
            width: '100%',
            minWidth: '120px',
            '&:focus': {
                outline: 'none',
                borderColor: '#007AAA',
                boxShadow: '0 0 0 2px rgba(0, 122, 170, 0.1)',
            },
        },
        '& span': {
            color: 'var(--spara-text-muted, #666)',
            fontSize: '0.85rem',
            fontWeight: 500,
        },
    },
    statsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        padding: '8px 0',
        '& .stat-item': {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.875rem',
            color: 'var(--spara-text-muted, #666)',
            '& .dot': {
                width: '8px',
                height: '8px',
                borderRadius: '50%',
            },
            '& .label': {
                fontWeight: 500,
            },
            '& .value': {
                fontWeight: 600,
                color: 'var(--spara-text-strong, #333)',
            },
        },
    },
    regionTable: {
        '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: '0 8px',
        },
        '& .MuiTableCell-root': {
            border: 'none',
            padding: '12px 16px',
            fontSize: '0.875rem',
        },
        '& .MuiTableHead-root': {
            '& .MuiTableCell-root': {
                color: 'var(--spara-text-muted, #666)',
                fontWeight: 500,
                paddingBottom: '4px',
            },
        },
        '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
                backgroundColor: 'var(--spara-surface, #fff)',
                borderRadius: '8px',
                boxShadow: 'var(--spara-shadow, 0 1px 3px rgba(0,0,0,0.1))',
                '&:hover': {
                    backgroundColor: 'var(--spara-surface-hover, #f8f9fa)',
                },
            },
        },
    },
    regionName: {
        fontWeight: 500,
        color: 'var(--spara-text-strong, #333)',
    },
    regionCount: {
        fontWeight: 600,
        color: '#007AAA',
    },
    regionPercentage: {
        color: 'var(--spara-text-muted, #666)',
        fontSize: '0.75rem',
    },
    segmentedControl: {
        width: '100%',
        marginBottom: '8px',
        '& .MuiToggleButton-root': {
            flex: 1,
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            padding: '4px 12px',
            gap: '6px',
            borderColor: 'var(--spara-border, #ddd)',
            color: 'var(--spara-text-muted, #666)',
            '&.Mui-selected': {
                backgroundColor: '#007AAA',
                color: '#fff',
                borderColor: '#007AAA',
                '&:hover': {
                    backgroundColor: '#005f7f',
                },
                '& .MuiSvgIcon-root': {
                    color: '#fff',
                },
            },
            '& .MuiSvgIcon-root': {
                fontSize: '1rem',
                color: 'var(--spara-text-muted, #888)',
            },
        },
    },
    selectorMenuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    entityLogo: {
        width: 24,
        height: 24,
        borderRadius: 4,
        objectFit: 'contain',
        backgroundColor: 'var(--spara-surface-2, #f5f5f5)',
    },
    entityLogoGroup: {
        width: 32,
        height: 24,
        borderRadius: 4,
        objectFit: 'cover',
        backgroundColor: 'var(--spara-surface-2, #f5f5f5)',
    },
    entityLogoGroupSelected: {
        height: 96,
        width: 'auto',
        borderRadius: 6,
        objectFit: 'contain',
        backgroundColor: 'var(--spara-surface-2, #f5f5f5)',
    },
    staticEntity: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 0',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: 'var(--spara-text-strong, #333)',
    },
    staticEntityLogo: {
        width: 28,
        height: 28,
        borderRadius: 4,
        objectFit: 'contain',
        backgroundColor: 'var(--spara-surface-2, #f5f5f5)',
    },
    staticEntityLogoGroup: {
        width: 120,
        height: 80,
        borderRadius: 6,
        objectFit: 'cover',
        backgroundColor: 'var(--spara-surface-2, #f5f5f5)',
    },
    scopeDescription: {
        margin: '0 0 12px 0',
        padding: '8px 14px',
        backgroundColor: 'var(--spara-surface-2, #f8f9fa)',
        borderRadius: '8px',
        border: '1px solid var(--spara-border, #e9ecef)',
        fontSize: '0.875rem',
        color: 'var(--spara-text-muted, #555)',
        fontStyle: 'italic',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
    },
    topStatsRow: {
        '& > *': {
            flex: '1 1 200px',
            minWidth: 0,
        },
        '& > div': {
            background: 'var(--spara-bg-elevated, var(--spara-surface)) !important',
        },
    },
    noDataPlaceholder: {
        height: '100%',
        minHeight: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 8,
        color: 'var(--spara-text-muted, #aaa)',
        fontSize: '0.9rem',
        '& svg': {
            fontSize: 32,
            opacity: 0.4,
        },
    },
    memberStats: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        marginTop: '10px',
        padding: '8px',
        backgroundColor: 'var(--spara-surface-2, #f8f9fa)',
        borderRadius: '6px',
    },
    memberStatRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.8rem',
        color: 'var(--spara-text-muted, #555)',
        '& .member-stat-label': {
            fontWeight: 400,
        },
        '& .member-stat-value': {
            fontWeight: 700,
            color: 'var(--spara-primary, #de2c48)',
            fontSize: '0.9rem',
        },
    },
    memberStatDivider: {
        height: '1px',
        backgroundColor: 'var(--spara-border, #e9ecef)',
        margin: '2px 0',
    },
    cardCategory2: {
        fontSize: '1.25rem',
        fontWeight: 400,
        color: 'var(--spara-primary, #de2c48)',
        fontStyle: 'italic',
        marginBottom: '0px',
        textAlign: 'center',
    },
    cardCategory3: {
        fontSize: '1.25rem',
        fontWeight: 400,
        color: 'var(--spara-primary, #de2c48)',
        fontWeight: 600,
        fontStyle: 'italic',
        marginTop: '4px',
        marginBottom: '12px',
    },
    cardTitle: {
        fontSize: '2rem',
        fontWeight: 600,
        color: 'var(--spara-primary, #de2c48)',
        margin: '0px',
        marginTop: '10px',
        textAlign: 'center',
    },
    cardTitleSkeleton: {
        height: '2.8rem',
        marginTop: '10px',
        borderRadius: '8px',
        background:
            'linear-gradient(90deg, rgba(222,44,72,0.08) 25%, rgba(222,44,72,0.18) 50%, rgba(222,44,72,0.08) 75%)',
        backgroundSize: '200% 100%',
        animation: '$shimmer 1.4s ease-in-out infinite',
        width: '70%',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    '@keyframes shimmer': {
        '0%': { backgroundPosition: '200% 0' },
        '100%': { backgroundPosition: '-200% 0' },
    },
});

const Dashboard = ({ t }) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { theme: resolvedTheme, isDark } = useThemeMode();

    const chartColors = useMemo(
        () => ({
            tick: readCssVar('--spara-text-muted', isDark ? '#b6bfcf' : '#666'),
            grid: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        }),
        [resolvedTheme, isDark]
    );

    const store = JSON.parse(localStorage.getItem('user'));
    const isSuperUser =
        store?.roles &&
        !store.roles.includes('company_user') &&
        !store.roles.includes('group_admin');
    const isCompanyUser = !isSuperUser;
    const isGroupAdmin = store?.roles?.length === 1 && store?.roles?.[0] === 'group_admin';
    const userHasTwoRoles =
        store?.roles?.length > 1 &&
        store.roles.includes('group_admin') &&
        store.roles.includes('company_user');
    const companies = isCompanyUser
        ? store?.companies?.filter((item) => item.id !== 1305 || item.id === null) || []
        : [];

    const [acFaStatus, setAcFaStatus] = useState('week');
    const [newUserStatus, setNewUserStatus] = useState('week');
    const [specialUsersSortBy, setSpecialUsersSortBy] = useState('users_count');

    const [companyUserId, setCompanyUserId] = useState(store?.user?.torgid_company_id);
    const [groupId, setGroupId] = useState(store?.groupIds?.[0]);
    const [companyGroupId, setCompanyGroupId] = useState(
        userHasTwoRoles || isGroupAdmin ? store?.groupIds?.[0] : store?.user?.torgid_company_id
    );
    const [companyGroupType, setCompanyGroupType] = useState(userHasTwoRoles || isGroupAdmin);
    const [selectedType, setSelectedType] = useState(
        userHasTwoRoles || isGroupAdmin ? 'group' : 'company'
    );

    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const [startDate, setStartDate] = useState(thirtyDaysAgo);
    const [endDate, setEndDate] = useState(today);
    const [mapReady, setMapReady] = useState(false);
    const [topOffersDays, setTopOffersDays] = useState(30);
    const [showInactiveOffers, setShowInactiveOffers] = useState(false);
    const [topOffersCompanyId, setTopOffersCompanyId] = useState(null);
    const [selectedMemberGroupId, setSelectedMemberGroupId] = useState(null);
    const [topOffersCompanyValue, setTopOffersCompanyValue] = useState(null);
    const [topOffersCompanyInput, setTopOffersCompanyInput] = useState('');
    const [topOffersCompanySearch, setTopOffersCompanySearch] = useState('');

    const cuDashboardId = (() => {
        if (!isCompanyUser) return null;
        if (isGroupAdmin || userHasTwoRoles) {
            const isGroup = !!companyGroupType;
            const id = companyGroupId || (isGroup ? groupId : companyUserId);
            return id ? { type: isGroup ? 'group' : 'company', id } : null;
        }
        return companyUserId ? { type: 'company', id: companyUserId } : null;
    })();

    const cuCountsUrl = cuDashboardId
        ? cuDashboardId.type === 'group'
            ? dashboardApi.groupCounts(cuDashboardId.id)
            : dashboardApi.cuCounts(cuDashboardId.id)
        : null;
    const cuActivityUrl = cuDashboardId
        ? cuDashboardId.type === 'group'
            ? dashboardApi.groupActivity(cuDashboardId.id)
            : dashboardApi.cuActivity(cuDashboardId.id)
        : null;
    const cuDemographicsUrl = cuDashboardId
        ? cuDashboardId.type === 'group'
            ? dashboardApi.groupDemographics(cuDashboardId.id)
            : dashboardApi.cuDemographics(cuDashboardId.id)
        : null;
    const cuLocationsUrl = cuDashboardId
        ? cuDashboardId.type === 'group'
            ? dashboardApi.groupLocations(cuDashboardId.id)
            : dashboardApi.cuLocations(cuDashboardId.id)
        : null;

    const transformActivityData = (data) => {
        if (!data?.results) return data;
        const nameOfMonth = new Date().toLocaleString('default', { month: 'long' }).toLowerCase();
        const results = { ...data.results };
        if (results.activates_and_favourites_by_year && results.offer_activates_current_month) {
            results.activates_and_favourites_by_year = results.activates_and_favourites_by_year.map(
                (item) =>
                    item.month === nameOfMonth
                        ? { ...item, activates: results.offer_activates_current_month }
                        : item
            );
        }
        return { ...data, results };
    };

    const cuCacheRef = useRef({});
    const [cuCounts, setCuCounts] = useState(null);
    const [cuActivity, setCuActivity] = useState(null);
    const [cuDemographics, setCuDemographics] = useState(null);
    const [cuLocations, setCuLocations] = useState(null);

    const makeCuFetchEffect = (url, setter, transform) => {
        if (!url) {
            setter(null);
            return;
        }
        if (cuCacheRef.current[url]) {
            setter(cuCacheRef.current[url]);
            return;
        }
        setter(null);
        let cancelled = false;
        fetcher(url)
            .then(transform || ((d) => d))
            .then((data) => {
                cuCacheRef.current[url] = data;
                if (!cancelled) setter(data);
            })
            .catch((err) => {
                console.error('Dashboard fetch error:', url, err);
                if (!cancelled) setter({ results: null, error: true });
            });
        return () => {
            cancelled = true;
        };
    };

    useEffect(() => makeCuFetchEffect(cuCountsUrl, setCuCounts, null), [cuCountsUrl]);
    useEffect(() => makeCuFetchEffect(cuActivityUrl, setCuActivity, transformActivityData), [
        cuActivityUrl,
    ]);
    useEffect(() => makeCuFetchEffect(cuDemographicsUrl, setCuDemographics, null), [
        cuDemographicsUrl,
    ]);
    useEffect(() => makeCuFetchEffect(cuLocationsUrl, setCuLocations, null), [cuLocationsUrl]);

    const { data: specialCompaniesData } = useSWR(
        isCompanyUser ? '/special_company/by_user/' : null,
        fetcher
    );
    const specialCompanies = specialCompaniesData ?? {};

    useEffect(() => {
        if (!isCompanyUser) return;
        const groupIds = store?.groupIds ?? [];
        const companyIds = (store?.companies ?? []).map((c) => c.id).filter(Boolean);
        groupIds.forEach((id) => {
            [
                dashboardApi.groupCounts(id),
                dashboardApi.groupActivity(id),
                dashboardApi.groupDemographics(id),
                dashboardApi.groupLocations(id),
            ].forEach((url) => {
                if (!cuCacheRef.current[url]) {
                    quietFetcher(url)
                        .then((data) => {
                            cuCacheRef.current[url] = data;
                        })
                        .catch(() => {});
                }
            });
        });
        companyIds.forEach((id) => {
            [
                dashboardApi.cuCounts(id),
                dashboardApi.cuActivity(id),
                dashboardApi.cuDemographics(id),
                dashboardApi.cuLocations(id),
            ].forEach((url) => {
                if (!cuCacheRef.current[url]) {
                    quietFetcher(url)
                        .then((data) => {
                            cuCacheRef.current[url] = data;
                        })
                        .catch(() => {});
                }
            });
        });
    }, []);

    useEffect(() => {
        if (!specialCompaniesData?.results) return;
        specialCompaniesData.results.forEach((group) => {
            if (group.id) {
                [
                    dashboardApi.groupCounts(group.id),
                    dashboardApi.groupActivity(group.id),
                    dashboardApi.groupDemographics(group.id),
                    dashboardApi.groupLocations(group.id),
                ].forEach((url) => {
                    if (!cuCacheRef.current[url]) {
                        quietFetcher(url)
                            .then((data) => {
                                cuCacheRef.current[url] = data;
                            })
                            .catch(() => {});
                    }
                });
            }
        });
    }, [specialCompaniesData]);

    useEffect(() => {
        const timer = setTimeout(() => setMapReady(true), 650);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setTopOffersCompanySearch(topOffersCompanyInput), 300);
        return () => clearTimeout(timer);
    }, [topOffersCompanyInput]);

    const { data: basicCounts, isLoading: basicCountsLoading } = useSWR(
        isSuperUser ? dashboardApi.basicCounts : null,
        fetcher
    );

    const { data: userDemographics, isLoading: userDemographicsLoading } = useSWR(
        isSuperUser ? dashboardApi.userDemographics : null,
        fetcher
    );

    const { data: newUsersData, isLoading: newUsersLoading } = useSWR(
        isSuperUser
            ? `new-users-${startDate?.toISOString().split('T')[0]}-${
                  endDate?.toISOString().split('T')[0]
              }`
            : null,
        () =>
            fetcher(
                dashboardApi.newUsersStatistics({
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                })
            )
    );

    const { data: offerActivityData, isLoading: offerActivityLoading } = useSWR(
        isSuperUser
            ? `offer-activity-${startDate?.toISOString().split('T')[0]}-${
                  endDate?.toISOString().split('T')[0]
              }`
            : null,
        () =>
            fetcher(
                dashboardApi.offerActivityStatistics({
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                })
            )
    );

    const { data: geographicData, isLoading: geographicDataLoading } = useSWR(
        isSuperUser ? dashboardApi.geographicData : null,
        fetcher
    );

    const { data: specialCompanyData, isLoading: specialCompanyDataLoading } = useSWR(
        isSuperUser ? dashboardApi.specialCompanyData : null,
        fetcher
    );

    const { data: companySearchResults } = useSWR(
        isSuperUser && topOffersCompanySearch
            ? companyApi.list({ search: topOffersCompanySearch, limit: 10 })
            : null,
        fetcher
    );

    const activeGroupId = isGroupAdmin ? groupId : companyGroupType ? companyGroupId : null;

    const currentGroup = specialCompanies?.results?.find((g) => g.id === activeGroupId);
    const isGoldenGroup = currentGroup?.type === 'golden';
    const showGoldenMemberGroups =
        (isGroupAdmin || (userHasTwoRoles && companyGroupType)) && isGoldenGroup;
    const { data: memberGroupsData, isLoading: memberGroupsLoading } = useSWR(
        showGoldenMemberGroups && activeGroupId
            ? dashboardApi.groupMemberGroups(activeGroupId)
            : null,
        fetcher
    );

    const topOffersGroupId = selectedMemberGroupId || activeGroupId;
    const topOffersCompanyFilter = isSuperUser
        ? topOffersCompanyId
        : cuDashboardId?.type === 'company'
        ? cuDashboardId.id
        : null;
    const { data: topOffersData, isLoading: topOffersLoading } = useSWR(
        topOffersGroupId
            ? dashboardApi.groupTopOffers(topOffersGroupId, topOffersDays, showInactiveOffers)
            : dashboardApi.topOffers(topOffersDays, showInactiveOffers, topOffersCompanyFilter),
        fetcher,
        { keepPreviousData: true }
    );

    const handleViewTypeChange = (type) => {
        setAcFaStatus(type);
        setNewUserStatus(type);
    };

    const setDateRange = (days) => {
        const end = new Date();
        const start = new Date();
        if (days === 'from_beginning') {
            start.setTime(new Date('2021-06-01').getTime());
        } else {
            start.setDate(end.getDate() - days);
        }
        setStartDate(start);
        setEndDate(end);
    };

    const getCurrentDuration = () => {
        if (!startDate || !endDate) return null;
        const beginningDate = new Date('2021-06-01');
        const startDateOnly = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate()
        );
        const beginningDateOnly = new Date(
            beginningDate.getFullYear(),
            beginningDate.getMonth(),
            beginningDate.getDate()
        );
        if (startDateOnly.getTime() === beginningDateOnly.getTime()) {
            return 'from_beginning';
        }
        const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        const durations = [30, 60, 90, 365, 730];
        return durations.includes(daysDiff) ? daysDiff : null;
    };

    const handleCompanyChange = (data) => {
        setCompanyUserId(data);
        setCompanyGroupId(data);
        setCompanyGroupType(false);
    };

    const handleGroupChange = (data) => {
        setGroupId(data);
        setCompanyGroupId(data);
        setCompanyGroupType(true);
    };

    const handleCompanyGroupChange = (data, type) => {
        setCompanyGroupId(data);
        setCompanyGroupType(type === 'group');
    };

    const handleTypeChange = (_, newType) => {
        if (!newType) return;
        setSelectedType(newType);
        if (newType === 'company') {
            const firstCompanyId = companies[0]?.id;
            if (firstCompanyId) {
                handleCompanyGroupChange(firstCompanyId, 'company');
            }
        } else {
            const firstGroupId = specialCompanies?.results?.[0]?.id;
            if (firstGroupId) {
                handleCompanyGroupChange(firstGroupId, 'group');
            }
        }
    };

    const cuDataLoading = isCompanyUser && !!cuCountsUrl && !cuCounts;
    const cuActivityLoading = isCompanyUser && !!cuActivityUrl && !cuActivity;
    const cuDemographicsLoading = isCompanyUser && !!cuDemographicsUrl && !cuDemographics;
    const cuLocationsLoading = isCompanyUser && !!cuLocationsUrl && !cuLocations;

    // Normalized data sources
    const demographicsSource = isSuperUser ? userDemographics?.results : cuDemographics?.results;

    const genderData = demographicsSource?.genders?.filter?.((item) => item.name !== 'other') || [
        { name: 'Male', value: 0 },
        { name: 'Female', value: 0 },
    ];

    const agesData = demographicsSource?.ages
        ? Object.keys(demographicsSource.ages)
              .map((key) => ({
                  name: key,
                  value: demographicsSource.ages[key],
              }))
              .filter((item) => item.value > 0)
        : [{ name: '', value: 0 }];

    const regionData = demographicsSource?.region_of_users || [
        { name: 'Reykjanes', value: 0 },
        { name: 'Suðurland', value: 0 },
        { name: 'Höfuðborgarsvæðið', value: 0 },
    ];

    const offerLocations = isSuperUser
        ? geographicData?.results?.offer_locations
        : cuLocations?.results?.offer_locations;

    // Offer activity data
    let offerActivateAndFav;
    if (isSuperUser) {
        offerActivateAndFav = offerActivityData?.results
            ? acFaStatus === 'week'
                ? (offerActivityData.results || []).map((item) => ({
                      date: item.date,
                      activates: item.activates,
                      opens: item.opens,
                      favourites: item.favourites,
                  }))
                : (() => {
                      const monthlyData = {};
                      (offerActivityData.results || []).forEach((item) => {
                          const date = new Date(item.date);
                          const monthKey = `${date.getFullYear()}-${String(
                              date.getMonth() + 1
                          ).padStart(2, '0')}`;
                          const monthName = date.toLocaleDateString('is-IS', {
                              month: 'long',
                              year: 'numeric',
                          });
                          if (!monthlyData[monthKey]) {
                              monthlyData[monthKey] = {
                                  name: monthName,
                                  activates: 0,
                                  opens: 0,
                                  favourites: 0,
                              };
                          }
                          monthlyData[monthKey].activates += item.activates || 0;
                          monthlyData[monthKey].opens += item.opens || 0;
                          monthlyData[monthKey].favourites += item.favourites || 0;
                      });
                      return Object.values(monthlyData);
                  })()
            : [{ name: '', activates: 0, favourites: 0, opens: 0 }];
    } else {
        offerActivateAndFav = cuActivity?.results
            ? acFaStatus === 'week'
                ? cuActivity.results.activates_and_favourites_by_week || []
                : cuActivity.results.activates_and_favourites_by_year || []
            : [{ name: '', activates: 0, favourites: 0, opens: 0 }];
    }

    // New users data (super user only)
    const newUsers = newUsersData?.results
        ? newUserStatus === 'week'
            ? (newUsersData.results || []).map((item) => ({
                  date: item.date,
                  total: item.new_users,
              }))
            : (() => {
                  const monthlyData = {};
                  (newUsersData.results || []).forEach((item) => {
                      const date = new Date(item.date);
                      const monthKey = `${date.getFullYear()}-${String(
                          date.getMonth() + 1
                      ).padStart(2, '0')}`;
                      const monthName = date.toLocaleDateString('is-IS', {
                          month: 'long',
                          year: 'numeric',
                      });
                      if (!monthlyData[monthKey]) {
                          monthlyData[monthKey] = { name: monthName, total: 0 };
                      }
                      monthlyData[monthKey].total += item.new_users || 0;
                  });
                  return Object.values(monthlyData);
              })()
        : [{ date: '', total: 0 }];

    // Region table data (super user only)
    const offerByRegiontableData = !geographicData?.results?.regions
        ? [[]]
        : (geographicData.results.regions || [])
              ?.slice()
              ?.sort((a, b) => (b.count || 0) - (a.count || 0))
              ?.map?.((value) => {
                  const total = (geographicData.results.regions || []).reduce(
                      (sum, item) => sum + (item.count || 0),
                      0
                  );
                  const percentage = (((value.count || 0) / total) * 100).toFixed(1);
                  return [
                      <span className={classes.regionName}>{value.value}</span>,
                      <div
                          style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: '8px',
                          }}
                      >
                          <span className={classes.regionPercentage}>({percentage}%)</span>
                          <span className={classes.regionCount}>{value.count || 0}</span>
                      </div>,
                  ];
              }) || [];

    const COLORS = [
        '#BB001D',
        '#008353',
        '#D09600',
        '#FF8042',
        '#023e8a',
        '#16B4F3',
        '#DB18EC',
        '#F2CB04',
        '#2D9CDB',
        '#6C5CE7',
        '#00B894',
    ];

    // Loading states
    const isOfferActivityLoading = isSuperUser ? offerActivityLoading : cuActivityLoading;
    const isDemographicsLoading = isSuperUser ? userDemographicsLoading : cuDemographicsLoading;
    const isMapLoading = isSuperUser ? geographicDataLoading : cuLocationsLoading;

    // Chart label helpers
    const offerActivityLabels = offerActivateAndFav.map((item) => {
        if (acFaStatus === 'week') return item.date;
        if (isSuperUser) return item.name;
        return t(item.month);
    });

    // Chart configurations
    const newUsersChartData = {
        labels: newUsers.map((item) => (newUserStatus === 'week' ? item.date : item.name)),
        datasets: [
            {
                label: t('new_users'),
                data: newUsers.map((item) => item.total),
                backgroundColor: '#007AAA',
                borderColor: '#007AAA',
                borderWidth: 0.0,
                borderRadius: 0,
                barThickness: 'flex',
                maxBarThickness: 30,
            },
        ],
    };

    const newUsersChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                displayColors: false,
                callbacks: {
                    label: (context) => `${t('new_users')}: ${context.raw}`,
                },
            },
            zoom: {
                zoom: { wheel: { enabled: false }, pinch: { enabled: false }, mode: 'x' },
                pan: { enabled: false, mode: 'x' },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: chartColors.tick, maxRotation: 45, minRotation: 45 },
            },
            y: {
                grid: { color: chartColors.grid },
                ticks: { color: chartColors.tick },
                beginAtZero: true,
            },
        },
        interaction: { intersect: false, mode: 'index' },
    };

    const offerActivateAndFavChartData = {
        labels: offerActivityLabels,
        datasets: [
            {
                label: t('dash.opens'),
                data: offerActivateAndFav.map((item) => item.opens),
                backgroundColor: '#16B4F3',
                borderColor: '#16B4F3',
                borderWidth: 0,
                borderRadius: 0,
                barThickness: 'flex',
                maxBarThickness: 30,
            },
            {
                label: t('dash.activates'),
                data: offerActivateAndFav.map((item) => item.activates),
                backgroundColor: '#008B6B',
                borderColor: '#008B6B',
                borderWidth: 0,
                borderRadius: 0,
                barThickness: 'flex',
                maxBarThickness: 30,
            },
            {
                label: t('dash.favourites'),
                data: offerActivateAndFav.map((item) => item.favourites),
                backgroundColor: '#DE2C48',
                borderColor: '#DE2C48',
                borderWidth: 0,
                borderRadius: 0,
                barThickness: 'flex',
                maxBarThickness: 30,
            },
        ],
    };

    const offerActivateAndFavChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${context.raw}`,
                },
            },
            zoom: {
                zoom: { wheel: { enabled: false }, pinch: { enabled: false }, mode: 'x' },
                pan: { enabled: false, mode: 'x' },
            },
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
                ticks: { color: chartColors.tick, maxRotation: 45, minRotation: 45 },
            },
            y: {
                stacked: true,
                grid: { color: chartColors.grid },
                ticks: { color: chartColors.tick },
                beginAtZero: true,
            },
        },
        interaction: { intersect: false, mode: 'index' },
    };

    const hasNewUsersData = newUsers.some((item) => (item.total || 0) > 0);

    // For company users, also check pre-computed summary counts since the daily array
    // can be all-zeros even when there is aggregate activity
    const cuWeekTotals =
        (cuActivity?.results?.offer_opened_by_week_count || 0) +
        (cuActivity?.results?.offer_activates_by_week_count || 0) +
        (cuActivity?.results?.user_favourites_by_week_count || 0);
    const cuYearTotals =
        (cuActivity?.results?.offer_opened_by_year_count || 0) +
        (cuActivity?.results?.offer_activates_by_year_count || 0) +
        (cuActivity?.results?.user_favourites_by_year_count || 0);
    const hasOfferActivityData =
        offerActivateAndFav.some(
            (item) => (item.opens || 0) + (item.activates || 0) + (item.favourites || 0) > 0
        ) || (isCompanyUser && acFaStatus === 'week' ? cuWeekTotals > 0 : cuYearTotals > 0);

    // Footer totals: use pre-computed API counts for company users, reduce array for super users
    const footerOpens = isCompanyUser
        ? acFaStatus === 'week'
            ? cuActivity?.results?.offer_opened_by_week_count || 0
            : cuActivity?.results?.offer_opened_by_year_count || 0
        : offerActivateAndFav.reduce((sum, item) => sum + (item.opens || 0), 0);
    const footerActivates = isCompanyUser
        ? acFaStatus === 'week'
            ? cuActivity?.results?.offer_activates_by_week_count || 0
            : cuActivity?.results?.offer_activates_by_year_count || 0
        : offerActivateAndFav.reduce((sum, item) => sum + (item.activates || 0), 0);
    const footerFavourites = isCompanyUser
        ? acFaStatus === 'week'
            ? cuActivity?.results?.user_favourites_by_week_count || 0
            : cuActivity?.results?.user_favourites_by_year_count || 0
        : offerActivateAndFav.reduce((sum, item) => sum + (item.favourites || 0), 0);

    const hasGenderData = genderData.some((item) => (item.value || 0) > 0);
    const hasAgesData = agesData.some((item) => (item.value || 0) > 0);
    const hasRegionData = regionData.some((item) => (item.value || 0) > 0);

    const genderChartData = {
        labels: genderData.map((item) => t(item.name.toLowerCase())),
        datasets: [
            {
                data: genderData.map((item) => item.value),
                backgroundColor: ['#DB18EC', '#007AAA'],
                borderWidth: 0,
            },
        ],
    };

    const genderChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                callbacks: {
                    label: (context) => {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((context.raw / total) * 100).toFixed(1);
                        return `${context.label}: ${context.raw} (${percentage}%)`;
                    },
                },
            },
        },
    };

    const agesTotalValue = agesData.reduce((sum, item) => sum + (item.value || 0), 0);

    const agesChartData = {
        labels: agesData.map((item) => item.name),
        datasets: [
            {
                data: agesData.map((item) => item.value),
                backgroundColor: COLORS,
                borderWidth: 0,
            },
        ],
    };

    const agesChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                callbacks: {
                    label: (context) => {
                        const pct =
                            agesTotalValue > 0
                                ? ((context.raw / agesTotalValue) * 100).toFixed(1)
                                : 0;
                        return `${context.label}: ${pct}%`;
                    },
                },
            },
        },
    };

    const regionChartData = {
        labels: regionData.map((item) => item.name),
        datasets: [
            {
                data: regionData.map((item) => item.value),
                backgroundColor: COLORS,
                borderWidth: 0,
            },
        ],
    };

    const regionChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: { size: 12 },
                },
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                callbacks: {
                    label: (context) => {
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((context.raw / total) * 100).toFixed(1);
                        return `${context.label}: ${context.raw} (${percentage}%)`;
                    },
                },
            },
        },
    };

    const dateRangeControls = (
        <div className={classes.dateControls}>
            <div className={classes.dateRangeButtons}>
                {[
                    { days: 30, label: '30 dagar' },
                    { days: 60, label: '60 dagar' },
                    { days: 90, label: '90 dagar' },
                    { days: 365, label: '1 ár' },
                    { days: 730, label: '2 ár' },
                    { days: 'from_beginning', label: 'Frá upphafi' },
                ].map(({ days, label }) => (
                    <MuiButton
                        key={days}
                        variant={getCurrentDuration() === days ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setDateRange(days)}
                    >
                        {label}
                    </MuiButton>
                ))}
            </div>
            <div className={classes.dateInputs}>
                <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="dd.MM.yyyy"
                    isClearable={false}
                    placeholderText="Start date"
                />
                <span>til</span>
                <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="dd.MM.yyyy"
                    isClearable={false}
                    placeholderText="End date"
                />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--spara-text-muted, #666)', fontWeight: 500 }}>
                    Sameina eftir:
                </div>
                <ButtonGroup
                    className={classes.buttonGroup}
                    variant="outlined"
                    aria-label="outlined button group"
                >
                    <MuiButton
                        variant={acFaStatus === 'week' ? 'contained' : 'outlined'}
                        onClick={() => handleViewTypeChange('week')}
                    >
                        {t('day')}
                    </MuiButton>
                    <MuiButton
                        variant={acFaStatus === 'year' ? 'contained' : 'outlined'}
                        onClick={() => handleViewTypeChange('year')}
                    >
                        {t('month')}
                    </MuiButton>
                </ButtonGroup>
            </div>
        </div>
    );

    return (
        <div>
            {/* Top stats row */}
            <div
                className={classes.topStatsRow}
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                    marginBottom: 16,
                    alignItems: 'stretch',
                }}
            >
                {isSuperUser && (
                    <>
                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={classes.cardCategory2}>{t('dash.users')}</p>
                                <h3 className={classes.cardTitle}>
                                    {basicCountsLoading ? (
                                        <div className={classes.cardTitleSkeleton} />
                                    ) : (
                                        formatWithThousandSeparator(
                                            basicCounts?.results?.users || 0
                                        )
                                    )}
                                </h3>
                            </CardHeader>
                        </Card>

                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={classes.cardCategory2}>
                                    {t('dash.users_last_month')}
                                </p>
                                <h3 className={classes.cardTitle}>
                                    {basicCountsLoading ? (
                                        <div className={classes.cardTitleSkeleton} />
                                    ) : (
                                        formatWithThousandSeparator(
                                            basicCounts?.results?.users_last_month || 0
                                        )
                                    )}
                                </h3>
                            </CardHeader>
                        </Card>

                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={classes.cardCategory2}>{t('dash.companies')}</p>
                                <h3 className={classes.cardTitle}>
                                    {basicCountsLoading ? (
                                        <div className={classes.cardTitleSkeleton} />
                                    ) : (
                                        formatWithThousandSeparator(
                                            basicCounts?.results?.companies || 0
                                        )
                                    )}
                                </h3>
                            </CardHeader>
                        </Card>

                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={classes.cardCategory2}>{t('dash.offers')}</p>
                                <h3 className={classes.cardTitle}>
                                    {basicCountsLoading ? (
                                        <div className={classes.cardTitleSkeleton} />
                                    ) : (
                                        formatWithThousandSeparator(
                                            basicCounts?.results?.offers || 0
                                        )
                                    )}
                                </h3>
                            </CardHeader>
                        </Card>

                        <Card style={{ margin: 0 }}>
                            <CardHeader>
                                <p className={classes.cardCategory2}>
                                    {t('dash.most_used_screen')}
                                </p>
                                <h3 className={classes.cardTitle}>
                                    {basicCountsLoading ? (
                                        <div className={classes.cardTitleSkeleton} />
                                    ) : (
                                        basicCounts?.results?.most_used_screen?.screen || '—'
                                    )}
                                </h3>
                                {!basicCountsLoading &&
                                basicCounts?.results?.most_used_screen?.opens != null ? (
                                    <p className={classes.cardCategory2} style={{ marginTop: 4 }}>
                                        {t('dash.most_used_screen_opens', {
                                            count: formatWithThousandSeparator(
                                                basicCounts.results.most_used_screen.opens
                                            ),
                                        })}
                                    </p>
                                ) : null}
                            </CardHeader>
                        </Card>
                    </>
                )}

                {isCompanyUser && (
                    <>
                        {userHasTwoRoles && companies && specialCompanies && (
                            <Card style={{ margin: 0 }}>
                                <CardHeader>
                                    <ToggleButtonGroup
                                        value={selectedType}
                                        exclusive
                                        onChange={handleTypeChange}
                                        className={classes.segmentedControl}
                                    >
                                        <ToggleButton value="group">
                                            <Groups />
                                            {t('menu.groups')}
                                        </ToggleButton>
                                        <ToggleButton value="company">
                                            <Business />
                                            {t('menu.companies')}
                                        </ToggleButton>
                                    </ToggleButtonGroup>

                                    {selectedType === 'company' ? (
                                        companies.length > 1 ? (
                                            <Select
                                                value={companyGroupId}
                                                onChange={(e) =>
                                                    handleCompanyGroupChange(
                                                        e.target.value,
                                                        'company'
                                                    )
                                                }
                                                fullWidth
                                                size="small"
                                                renderValue={(selected) => {
                                                    const c = companies.find(
                                                        (item) => item.id === selected
                                                    );
                                                    if (!c) return '';
                                                    return (
                                                        <span className={classes.selectorMenuItem}>
                                                            {c.logo && (
                                                                <img
                                                                    src={c.logo}
                                                                    alt=""
                                                                    className={classes.entityLogo}
                                                                />
                                                            )}
                                                            {c.name}
                                                        </span>
                                                    );
                                                }}
                                            >
                                                {companies.map((value, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={value.id}
                                                        className={classes.selectorMenuItem}
                                                    >
                                                        {value.logo && (
                                                            <img
                                                                src={value.logo}
                                                                alt=""
                                                                className={classes.entityLogo}
                                                            />
                                                        )}
                                                        {value.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <div className={classes.staticEntity}>
                                                {companies[0]?.logo && (
                                                    <img
                                                        src={companies[0].logo}
                                                        alt=""
                                                        className={classes.staticEntityLogo}
                                                    />
                                                )}
                                                <span>{companies[0]?.name}</span>
                                            </div>
                                        )
                                    ) : specialCompanies?.results?.length > 1 ? (
                                        <Select
                                            value={companyGroupId}
                                            onChange={(e) =>
                                                handleCompanyGroupChange(e.target.value, 'group')
                                            }
                                            fullWidth
                                            size="small"
                                            renderValue={(selected) => {
                                                const g = specialCompanies?.results?.find(
                                                    (item) => item.id === selected
                                                );
                                                if (!g) return '';
                                                return (
                                                    <span className={classes.selectorMenuItem}>
                                                        {g.logo && (
                                                            <img
                                                                src={g.logo}
                                                                alt=""
                                                                className={
                                                                    classes.entityLogoGroupSelected
                                                                }
                                                            />
                                                        )}
                                                        {g.name}
                                                    </span>
                                                );
                                            }}
                                        >
                                            {specialCompanies.results.map((value, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={value.id}
                                                    className={classes.selectorMenuItem}
                                                >
                                                    {value.logo && (
                                                        <img
                                                            src={value.logo}
                                                            alt=""
                                                            className={classes.entityLogoGroup}
                                                        />
                                                    )}
                                                    {value.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    ) : (
                                        <div className={classes.staticEntity}>
                                            {specialCompanies?.results?.[0]?.logo && (
                                                <img
                                                    src={specialCompanies.results[0].logo}
                                                    alt=""
                                                    className={classes.staticEntityLogoGroup}
                                                />
                                            )}
                                            <span>{specialCompanies?.results?.[0]?.name}</span>
                                        </div>
                                    )}
                                </CardHeader>
                            </Card>
                        )}

                        {!userHasTwoRoles && companies.length > 1 && (
                            <Card style={{ margin: 0 }}>
                                <CardHeader>
                                    <p className={classes.cardCategory2}>{t('companies')}</p>
                                    <Select
                                        value={companyUserId}
                                        onChange={(e) => handleCompanyChange(e.target.value)}
                                        fullWidth
                                        size="small"
                                        style={{ marginTop: '8px' }}
                                    >
                                        {companies.map((value, index) => (
                                            <MenuItem key={index} value={value.id}>
                                                {value.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </CardHeader>
                            </Card>
                        )}

                        {isGroupAdmin && specialCompanies?.results?.length > 1 && (
                            <Card style={{ margin: 0 }}>
                                <CardHeader>
                                    <p className={classes.cardCategory2}>
                                        {t('special_companies')}
                                    </p>
                                    <Select
                                        value={groupId}
                                        onChange={(e) => handleGroupChange(e.target.value)}
                                        fullWidth
                                        size="small"
                                        style={{ marginTop: '8px' }}
                                    >
                                        {specialCompanies.results.map((value, index) => (
                                            <MenuItem key={index} value={value.id}>
                                                {value.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </CardHeader>
                            </Card>
                        )}

                        <div
                            style={{
                                overflow: 'hidden',
                                flexGrow: 1,
                                flexShrink: 1,
                                flexBasis: isGroupAdmin || companyGroupType ? 200 : 0,
                                maxWidth: isGroupAdmin || companyGroupType ? 600 : 0,
                                maxHeight: isGroupAdmin || companyGroupType ? 500 : 0,
                                opacity: isGroupAdmin || companyGroupType ? 1 : 0,
                                transition:
                                    'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), flex-basis 300ms cubic-bezier(0.4, 0, 0.2, 1), max-height 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            <Card
                                style={{
                                    margin: 0,
                                    height: '100%',
                                    minWidth: 200,
                                    cursor: 'pointer',
                                }}
                                onClick={() => navigate('/groups/members')}
                            >
                                <CardHeader>
                                    <p className={classes.cardCategory2}>{t('menu.members')}</p>
                                    <h3 className={classes.cardTitle}>
                                        {cuDataLoading ? (
                                            <Skeleton
                                                width={80}
                                                height={48}
                                                style={{ margin: '0 auto' }}
                                            />
                                        ) : (
                                            formatWithThousandSeparator(
                                                cuCounts?.results?.members ??
                                                    cuCounts?.results?.users ??
                                                    0
                                            )
                                        )}
                                    </h3>
                                    <div className={classes.memberStats}>
                                        <div className={classes.memberStatRow}>
                                            <span className="member-stat-label">
                                                {t('dash.members_registered')}
                                            </span>
                                            <span className="member-stat-value">
                                                {cuDataLoading ? (
                                                    <Skeleton width={48} height={22} />
                                                ) : (
                                                    formatWithThousandSeparator(
                                                        cuCounts?.results?.registered_members ?? '—'
                                                    )
                                                )}
                                            </span>
                                        </div>
                                        <div className={classes.memberStatDivider} />
                                        <div className={classes.memberStatRow}>
                                            <span className="member-stat-label">
                                                {t('dash.members_active_last_month')}
                                            </span>
                                            <span className="member-stat-value">
                                                {cuDataLoading ? (
                                                    <Skeleton width={48} height={22} />
                                                ) : (
                                                    formatWithThousandSeparator(
                                                        cuCounts?.results
                                                            ?.active_members_last_month ?? '—'
                                                    )
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </CardHeader>
                            </Card>
                        </div>

                        <Card
                            style={{ margin: 0, cursor: 'pointer' }}
                            onClick={() => navigate('/offers')}
                        >
                            <CardHeader>
                                <p className={classes.cardCategory2}>
                                    {isGroupAdmin || companyGroupType
                                        ? t('dash.cu_offers_group')
                                        : t('dash.cu_offers')}
                                </p>
                                <h3 className={classes.cardTitle}>
                                    {cuDataLoading ? (
                                        <Skeleton
                                            width={80}
                                            height={48}
                                            style={{ margin: '0 auto' }}
                                        />
                                    ) : cuCounts?.results?.offer_limit ? (
                                        `${cuCounts?.results?.offers} ${t('of')} ${
                                            cuCounts.results.offer_limit
                                        }`
                                    ) : (
                                        cuCounts?.results?.offers
                                    )}
                                </h3>
                            </CardHeader>
                        </Card>
                    </>
                )}
            </div>

            {isSuperUser && <AnalyticsLive />}

            {/* Charts row */}
            <div
                style={{
                    display: 'grid',
                    gap: 16,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    marginBottom: 16,
                }}
            >
                {isSuperUser && (
                    <Card style={{ margin: 0 }}>
                        <CardHeader>
                            <p className={classes.cardCategory3}>{t('new_users')}</p>
                            {dateRangeControls}
                        </CardHeader>
                        <CardBody>
                            {newUsersLoading ? (
                                <div
                                    style={{
                                        height: '300px',
                                        position: 'relative',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <SparaLoader />
                                </div>
                            ) : !hasNewUsersData ? (
                                <div className={classes.noDataPlaceholder} style={{ height: 300 }}>
                                    <BarChartOutlined />
                                    <span>{t('dash.no_data')}</span>
                                </div>
                            ) : (
                                <div
                                    style={{
                                        height: '300px',
                                        position: 'relative',
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <ChartJSBar
                                        key={`new-users-${resolvedTheme}-${newUserStatus}-${
                                            startDate?.toISOString().split('T')[0]
                                        }-${endDate?.toISOString().split('T')[0]}`}
                                        data={newUsersChartData}
                                        options={newUsersChartOptions}
                                        aria-label={t('new_users_chart')}
                                    />
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}

                <Card style={{ margin: 0 }}>
                    <CardHeader>
                        <p className={classes.cardCategory3}>{t('dash.offers_activity')}</p>
                        {isSuperUser ? (
                            dateRangeControls
                        ) : (
                            <ButtonGroup
                                className={classes.buttonGroup}
                                variant="outlined"
                                style={{ marginTop: '8px' }}
                            >
                                <MuiButton
                                    variant={acFaStatus === 'week' ? 'contained' : 'outlined'}
                                    onClick={() => setAcFaStatus('week')}
                                >
                                    {t('month')}
                                </MuiButton>
                                <MuiButton
                                    variant={acFaStatus === 'year' ? 'contained' : 'outlined'}
                                    onClick={() => setAcFaStatus('year')}
                                >
                                    {t('year')}
                                </MuiButton>
                            </ButtonGroup>
                        )}
                    </CardHeader>
                    <CardBody>
                        {isOfferActivityLoading || cuDataLoading ? (
                            <Skeleton
                                variant="rectangular"
                                height={300}
                                style={{ borderRadius: 8 }}
                            />
                        ) : !hasOfferActivityData ? (
                            <div className={classes.noDataPlaceholder} style={{ height: 300 }}>
                                <BarChartOutlined />
                                <span>{t('dash.no_data')}</span>
                            </div>
                        ) : (
                            <div style={{ height: '300px', position: 'relative' }}>
                                <ChartJSBar
                                    key={`offer-activity-${resolvedTheme}-${acFaStatus}-${
                                        startDate?.toISOString().split('T')[0]
                                    }-${endDate?.toISOString().split('T')[0]}`}
                                    data={offerActivateAndFavChartData}
                                    options={offerActivateAndFavChartOptions}
                                    aria-label={t('offers_activity_chart')}
                                />
                            </div>
                        )}
                    </CardBody>
                    <CardFooter stats className={classes.cardFooter} style={{ marginTop: '5px' }}>
                        <div className={classes.statsContainer}>
                            <div className="stat-item">
                                <div className="dot" style={{ backgroundColor: '#16B4F3' }} />
                                <span className="label">{t('dash.opens')}</span>
                                <span className="value">
                                    {cuDataLoading ? (
                                        <Skeleton width={40} height={20} />
                                    ) : (
                                        formatWithThousandSeparator(footerOpens)
                                    )}
                                </span>
                            </div>
                            <div className="stat-item">
                                <div className="dot" style={{ backgroundColor: '#008B6B' }} />
                                <span className="label">{t('dash.activates')}</span>
                                <span className="value">
                                    {cuDataLoading ? (
                                        <Skeleton width={40} height={20} />
                                    ) : (
                                        formatWithThousandSeparator(footerActivates)
                                    )}
                                </span>
                            </div>
                            <div className="stat-item">
                                <div className="dot" style={{ backgroundColor: '#DE2C48' }} />
                                <span className="label">{t('dash.favourites')}</span>
                                <span className="value">
                                    {cuDataLoading ? (
                                        <Skeleton width={40} height={20} />
                                    ) : (
                                        formatWithThousandSeparator(footerFavourites)
                                    )}
                                </span>
                            </div>
                        </div>
                    </CardFooter>
                </Card>

                {isCompanyUser && (
                    <Card style={{ margin: 0 }}>
                        <CardHeader>
                            <p className={classes.cardCategory3}>{t('dash.number_offers')}</p>
                        </CardHeader>
                        <CardBody>
                            {isMapLoading ? (
                                <div
                                    style={{
                                        height: 400,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <SparaLoader />
                                </div>
                            ) : (
                                <div style={{ height: 400 }}>
                                    {mapReady && <MapboxMap companies={offerLocations || []} />}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}
            </div>

            {/* Top offers */}
            <Card style={{ margin: '0 0 16px 0' }}>
                <CardHeader>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 8,
                        }}
                    >
                        <div>
                            <p className={classes.cardCategory3} style={{ margin: 0 }}>
                                {t('dash.top_offers')}
                                {selectedMemberGroupId &&
                                    (() => {
                                        const mg = memberGroupsData?.results?.find(
                                            (g) => g.id === selectedMemberGroupId
                                        );
                                        return mg ? (
                                            <span
                                                style={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 400,
                                                    color: 'var(--spara-primary, #E5576C)',
                                                    marginLeft: 8,
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setSelectedMemberGroupId(null)}
                                            >
                                                {mg.display_name || mg.name} ✕
                                            </span>
                                        ) : null;
                                    })()}
                            </p>
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: 13,
                                    color: 'var(--spara-text-muted, #999)',
                                    fontWeight: 400,
                                }}
                            >
                                {topOffersDays === 30
                                    ? t('dash.last_30_days')
                                    : topOffersDays === 60
                                    ? t('dash.last_60_days')
                                    : topOffersDays === 90
                                    ? t('dash.last_90_days')
                                    : t('dash.from_beginning')}
                            </p>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                flexWrap: 'wrap',
                            }}
                        >
                            {isSuperUser && (
                                <Autocomplete
                                    size="small"
                                    options={companySearchResults?.results || []}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={topOffersCompanyValue}
                                    onChange={(_, newValue) => {
                                        setTopOffersCompanyValue(newValue);
                                        setTopOffersCompanyId(newValue?.id || null);
                                    }}
                                    inputValue={topOffersCompanyInput}
                                    onInputChange={(_, newInputValue, reason) => {
                                        setTopOffersCompanyInput(newInputValue);
                                        if (reason === 'clear') {
                                            setTopOffersCompanyValue(null);
                                            setTopOffersCompanyId(null);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('menu.companies')}
                                            size="small"
                                        />
                                    )}
                                    style={{ minWidth: 200 }}
                                    clearOnEscape
                                    noOptionsText={
                                        topOffersCompanyInput
                                            ? t('dash.no_data')
                                            : t('dash.type_to_search')
                                    }
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                />
                            )}
                            <SparaButton
                                variant={showInactiveOffers ? 'filled' : 'outlined'}
                                color="warning"
                                size="small"
                                onClick={() => setShowInactiveOffers((v) => !v)}
                            >
                                {t('dash.show_inactive')}
                            </SparaButton>
                            <SparaButton
                                variant={topOffersDays === 30 ? 'filled' : 'outlined'}
                                color="secondary"
                                size="small"
                                onClick={() => setTopOffersDays(30)}
                            >
                                30 {t('days')}
                            </SparaButton>
                            <SparaButton
                                variant={topOffersDays === 60 ? 'filled' : 'outlined'}
                                color="secondary"
                                size="small"
                                onClick={() => setTopOffersDays(60)}
                            >
                                60 {t('days')}
                            </SparaButton>
                            <SparaButton
                                variant={topOffersDays === 90 ? 'filled' : 'outlined'}
                                color="secondary"
                                size="small"
                                onClick={() => setTopOffersDays(90)}
                            >
                                90 {t('days')}
                            </SparaButton>
                            <SparaButton
                                variant={topOffersDays === null ? 'filled' : 'outlined'}
                                color="secondary"
                                size="small"
                                onClick={() => setTopOffersDays(null)}
                            >
                                {t('dash.from_beginning')}
                            </SparaButton>
                        </div>
                    </div>
                </CardHeader>
                <CardBody>
                    {topOffersLoading && !topOffersData ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                            <SparaLoader />
                        </div>
                    ) : !topOffersData?.results?.length ? (
                        <div
                            style={{
                                textAlign: 'center',
                                padding: 24,
                                color: 'var(--spara-text-muted, #999)',
                                fontSize: 13,
                            }}
                        >
                            {t('dash.no_data')}
                        </div>
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0,
                                opacity: topOffersLoading ? 0.5 : 1,
                                transition: 'opacity 0.2s',
                            }}
                        >
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 80px 80px 80px',
                                    gap: 8,
                                    padding: '0 8px 8px',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: 'var(--spara-text-muted, #999)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                }}
                            >
                                <span>{t('offer')}</span>
                                <span style={{ textAlign: 'center' }}>{t('dash.opens')}</span>
                                <span style={{ textAlign: 'center' }}>{t('dash.activates')}</span>
                                <span style={{ textAlign: 'center' }}>{t('dash.favourites')}</span>
                            </div>
                            {topOffersData.results.map((offer, index) => {
                                const offerTitle =
                                    i18n.language === 'en' && offer.title_en
                                        ? offer.title_en
                                        : offer.title;
                                const inactive = !offer.is_active;
                                const rowBg =
                                    index % 2 === 0
                                        ? 'var(--spara-table-row-alt, #fafafa)'
                                        : 'var(--spara-table-row, #fff)';
                                return (
                                    <div
                                        key={offer.id}
                                        onClick={() => navigate(`/offers/${offer.id}/statistics`)}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 80px 80px 80px',
                                            gap: 8,
                                            alignItems: 'center',
                                            padding: '10px 8px',
                                            borderRadius: 8,
                                            cursor: 'pointer',
                                            background: rowBg,
                                            opacity: inactive ? 0.55 : 1,
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.background =
                                                'var(--spara-primary-soft, rgba(222,44,72,0.04))')
                                        }
                                        onMouseLeave={(e) =>
                                            (e.currentTarget.style.background = rowBg)
                                        }
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                minWidth: 0,
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: 'var(--spara-text-muted, #bbb)',
                                                    width: 20,
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {index + 1}
                                            </span>
                                            {offer.image && (
                                                <img
                                                    src={offer.image}
                                                    alt=""
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 6,
                                                        objectFit: 'cover',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                            <div
                                                style={{
                                                    overflow: 'hidden',
                                                    minWidth: 0,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 500,
                                                        color: inactive
                                                            ? 'var(--spara-text-muted, #999)'
                                                            : 'var(--spara-text-strong, #333)',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {offerTitle}
                                                </div>
                                                {offer.torgid_company?.name && (
                                                    <div
                                                        style={{
                                                            fontSize: 11,
                                                            fontWeight: 400,
                                                            color: 'var(--spara-text-muted, #999)',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {offer.torgid_company.name}
                                                    </div>
                                                )}
                                            </div>
                                            {inactive && (
                                                <span
                                                    style={{
                                                        fontSize: 10,
                                                        fontWeight: 600,
                                                        color: 'var(--spara-warning-text-strong, #f59e0b)',
                                                        background: 'var(--spara-warning-bg, #fffbeb)',
                                                        padding: '1px 7px',
                                                        borderRadius: 10,
                                                        flexShrink: 0,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {t('inactive')}
                                                </span>
                                            )}
                                        </div>
                                        <span
                                            style={{
                                                textAlign: 'center',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: '#16B4F3',
                                            }}
                                        >
                                            {formatWithThousandSeparator(offer.opens_count || 0)}
                                        </span>
                                        <span
                                            style={{
                                                textAlign: 'center',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: '#008B6B',
                                            }}
                                        >
                                            {formatWithThousandSeparator(
                                                offer.torgid_offer_activates_count || 0
                                            )}
                                        </span>
                                        <span
                                            style={{
                                                textAlign: 'center',
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: '#DE2C48',
                                            }}
                                        >
                                            {formatWithThousandSeparator(
                                                offer.torgid_user_favourites_count || 0
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {topOffersData?.results?.length > 0 && (
                        <div style={{ textAlign: 'center', paddingTop: 12 }}>
                            <SparaButton
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() =>
                                    navigate(
                                        `/offers?sort=activity${
                                            !showInactiveOffers ? '&display=true' : ''
                                        }${topOffersDays ? `&days=${topOffersDays}` : ''}${
                                            topOffersCompanyFilter
                                                ? `&company_id=${topOffersCompanyFilter}`
                                                : ''
                                        }`
                                    )
                                }
                            >
                                {t('dash.show_more')}
                            </SparaButton>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Map section - super user only (includes region table) */}
            {isSuperUser && (
                <Card style={{ margin: '16px 0px' }}>
                    <CardHeader>
                        <p className={classes.cardCategory3}>{t('dash.number_offers')}</p>
                    </CardHeader>
                    <CardBody>
                        {isMapLoading ? (
                            <div
                                style={{
                                    height: '400px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SparaLoader />
                            </div>
                        ) : (
                            <GridContainer justifyContent="space-between">
                                <GridItem xs={12} sm={12} md={4}>
                                    <Table
                                        tableData={offerByRegiontableData}
                                        className={classes.regionTable}
                                    />
                                </GridItem>
                                <GridItem xs={12} sm={12} md={8}>
                                    <div style={{ height: 400 }}>
                                        {mapReady && <MapboxMap companies={offerLocations || []} />}
                                    </div>
                                </GridItem>
                            </GridContainer>
                        )}
                    </CardBody>
                </Card>
            )}

            {/* Demographics row */}
            <div
                style={{
                    display: 'grid',
                    gap: 16,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    marginTop: 16,
                    marginBottom: 16,
                }}
            >
                <Card style={{ margin: 0 }}>
                    <CardHeader>
                        <p className={classes.cardCategory3}>{t('dash.user_ages')}</p>
                    </CardHeader>
                    <CardBody>
                        {isDemographicsLoading ? (
                            <div
                                style={{
                                    height: '200px',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SparaLoader />
                            </div>
                        ) : !hasAgesData ? (
                            <div className={classes.noDataPlaceholder} style={{ height: 200 }}>
                                <PieChartOutlined />
                                <span>{t('dash.no_data')}</span>
                            </div>
                        ) : (
                            <div style={{ height: '200px', position: 'relative' }}>
                                <ChartJSPie
                                    key="ages-chart"
                                    data={agesChartData}
                                    options={agesChartOptions}
                                    aria-label={t('user_ages_chart')}
                                />
                            </div>
                        )}
                    </CardBody>
                    <CardFooter stats className={classes.cardFooter}>
                        <div className={classes.statsContainer}>
                            {agesData.map((item, index) => (
                                <div className="stat-item" key={item.name}>
                                    <div
                                        className="dot"
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="label">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </CardFooter>
                </Card>

                <Card style={{ margin: 0 }}>
                    <CardHeader>
                        <p className={classes.cardCategory3}>{t('dash.user_region')}</p>
                    </CardHeader>
                    <CardBody>
                        {isDemographicsLoading ? (
                            <div
                                style={{
                                    height: '260px',
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SparaLoader />
                            </div>
                        ) : !hasRegionData ? (
                            <div className={classes.noDataPlaceholder} style={{ height: 260 }}>
                                <PieChartOutlined />
                                <span>{t('dash.no_data')}</span>
                            </div>
                        ) : (
                            <div style={{ height: '260px', position: 'relative' }}>
                                <ChartJSPie
                                    key="region-chart"
                                    data={regionChartData}
                                    options={regionChartOptions}
                                    aria-label={t('user_region_chart')}
                                />
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Golden group member groups (group admin only) */}
            {showGoldenMemberGroups &&
                (memberGroupsData?.results?.length > 0 || memberGroupsLoading) && (
                    <Card style={{ margin: '16px 0 0' }}>
                        <CardHeader>
                            <FlexContainer
                                style={{ alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}
                            >
                                <p
                                    className={classes.cardCategory3}
                                    style={{ padding: 0, margin: 0 }}
                                >
                                    {t('dash.golden_member_groups')}
                                </p>
                                {!memberGroupsLoading &&
                                    memberGroupsData?.results?.length > 0 &&
                                    (() => {
                                        const totalUsers = memberGroupsData.results.reduce(
                                            (sum, mg) => sum + (parseInt(mg.users_count) || 0),
                                            0
                                        );
                                        const totalMembers = memberGroupsData.results.reduce(
                                            (sum, mg) =>
                                                sum + (parseInt(mg.total_users_count) || 0),
                                            0
                                        );
                                        return (
                                            <div style={{ display: 'flex', gap: '16px' }}>
                                                <span
                                                    style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--spara-text-muted, #888)',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontWeight: 600,
                                                            color: 'var(--spara-primary, #E5576C)',
                                                        }}
                                                    >
                                                        {formatWithThousandSeparator(totalUsers)}
                                                    </span>{' '}
                                                    notendur
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--spara-text-muted, #888)',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            fontWeight: 600,
                                                            color: 'var(--spara-primary, #E5576C)',
                                                        }}
                                                    >
                                                        {formatWithThousandSeparator(totalMembers)}
                                                    </span>{' '}
                                                    heildarfjöldi
                                                </span>
                                            </div>
                                        );
                                    })()}
                            </FlexContainer>
                        </CardHeader>
                        <CardBody>
                            {memberGroupsLoading ? (
                                <div
                                    style={{
                                        height: '200px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <SparaLoader />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(165px, 1fr))',
                                        gap: '8px',
                                        padding: '4px',
                                    }}
                                >
                                    {(memberGroupsData?.results || [])
                                        .slice()
                                        .sort(
                                            (a, b) =>
                                                (parseInt(b.total_users_count) || 0) -
                                                (parseInt(a.total_users_count) || 0)
                                        )
                                        .map((mg) => (
                                            <div
                                                key={'goldenMG_' + mg.id}
                                                onClick={() =>
                                                    setSelectedMemberGroupId(
                                                        selectedMemberGroupId === mg.id
                                                            ? null
                                                            : mg.id
                                                    )
                                                }
                                                style={{
                                                    cursor: 'pointer',
                                                    outline:
                                                        selectedMemberGroupId === mg.id
                                                            ? '2px solid #E5576C'
                                                            : '2px solid transparent',
                                                    borderRadius: '4px',
                                                    transition: 'outline 0.15s',
                                                }}
                                            >
                                                <SpecialUserCard
                                                    name={mg.display_name || mg.name}
                                                    logo={mg.logo}
                                                    totalCount={parseInt(mg.total_users_count) || 0}
                                                    usersCount={parseInt(mg.users_count) || 0}
                                                />
                                            </div>
                                        ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                )}

            {/* Special section (super user only) */}
            {isSuperUser && (
                <div
                    style={{
                        display: 'grid',
                        gap: 16,
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    }}
                >
                    <Card style={{ margin: 0 }}>
                        <CardHeader>
                            <FlexContainer
                                style={{
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <p
                                    className={classes.cardCategory3}
                                    style={{ padding: 0, margin: 0 }}
                                >
                                    {t('dash.special_user')}
                                </p>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <select
                                        value={specialUsersSortBy}
                                        onChange={(e) => setSpecialUsersSortBy(e.target.value)}
                                        style={{
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            border: '1px solid var(--spara-input-border, #ddd)',
                                            fontSize: '0.9rem',
                                            color: 'var(--spara-text-muted, #666)',
                                            backgroundColor: 'var(--spara-input-bg, white)',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <option value="users_count">
                                            Raða eftir notendafjölda
                                        </option>
                                        <option value="total_users_count">
                                            Raða eftir heildarfjölda
                                        </option>
                                        <option value="percentage">Raða eftir hlutfalli</option>
                                    </select>
                                </div>
                            </FlexContainer>
                        </CardHeader>
                        <CardBody>
                            {specialCompanyDataLoading ? (
                                <div
                                    style={{
                                        height: '200px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <SparaLoader />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(165px, 1fr))',
                                        gap: '8px',
                                        padding: '4px',
                                    }}
                                >
                                    {(() => {
                                        const sortedUsers =
                                            specialCompanyData?.results?.special_users
                                                ?.slice()
                                                ?.sort((a, b) => {
                                                    if (specialUsersSortBy === 'percentage') {
                                                        const aTotal =
                                                            parseInt(a.total_users_count) || 0;
                                                        const bTotal =
                                                            parseInt(b.total_users_count) || 0;
                                                        const aUsers = parseInt(a.users_count) || 0;
                                                        const bUsers = parseInt(b.users_count) || 0;
                                                        const aPercentage =
                                                            aTotal > 0 ? aUsers / aTotal : 0;
                                                        const bPercentage =
                                                            bTotal > 0 ? bUsers / bTotal : 0;
                                                        return bPercentage - aPercentage;
                                                    }
                                                    const aValue =
                                                        parseInt(a[specialUsersSortBy]) || 0;
                                                    const bValue =
                                                        parseInt(b[specialUsersSortBy]) || 0;
                                                    return bValue - aValue;
                                                }) || [];

                                        return (sortedUsers || []).map?.((value, index) => (
                                            <SpecialUserCard
                                                key={'specialUsersKey' + index}
                                                name={value.name}
                                                logo={value.logo}
                                                totalCount={parseInt(value.total_users_count) || 0}
                                                usersCount={parseInt(value.users_count) || 0}
                                            />
                                        ));
                                    })()}
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    <Card style={{ margin: 0 }}>
                        <CardHeader>
                            <p className={classes.cardCategory3}>{t('dash.special_offers')}</p>
                        </CardHeader>
                        <CardBody>
                            {specialCompanyDataLoading ? (
                                <div
                                    style={{
                                        height: '200px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <SparaLoader />
                                </div>
                            ) : (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns:
                                            'repeat(auto-fill, minmax(165px, 1fr))',
                                        gap: '8px',
                                        padding: '4px',
                                    }}
                                >
                                    {(specialCompanyData?.results?.special_offers || []).map?.(
                                        (value, index) => (
                                            <SpecialUserCard
                                                key={'specialOffersKey' + index}
                                                name={value.name}
                                                logo={value.logo}
                                                singleValue={value.torgid_offers_count}
                                                singleValueLabel={t('common.offers')}
                                            />
                                        )
                                    )}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default withTranslation('', { withRef: true })(Dashboard);
